import axios, { AxiosError } from "axios";
import { IAxiosReturn } from "./interface";

export interface DownloadFileProps {
  url: string;
  fileName: string;
  fileType: string;
  blob: boolean;
  successMessage: string;
  errorMessage: string;
  controller: AbortController;
}

export type IFileReturn = IAxiosReturn<string>;

/**
 * Downloads a file from a given URL
 *
 * @param props - Destructured parameter object
 * @param props.url - The request URL to download the file from.
 * @param props.fileName - The filename to use when saving the downloaded file.
 * @param props.fileType - The MIME type for the downloaded file blob.
 * @param props.successMessage - Message returned on successful download.
 * @param props.errorMessage - Default error message used when the download fails.
 * @param props.blob - If true, request the response as a binary `Blob`; otherwise parse JSON/string.
 * @param props.controller - AbortController used to cancel the HTTP request.
 */
export const downloadFile = async ({
  url,
  fileName,
  fileType,
  successMessage,
  errorMessage,
  blob,
  controller,
}: DownloadFileProps): Promise<IFileReturn> => {
  try {
    const response = await axios.get(
      url,
      blob
        ? {
            responseType: "blob",
            signal: controller.signal,
          }
        : {
            signal: controller.signal,
          },
    );

    const fileContents = !blob ? JSON.stringify(response.data) : response.data;
    const downloadBlob = new Blob([fileContents], { type: fileType });

    // Create object URL and download
    const link = document.createElement("a");
    link.href = URL.createObjectURL(downloadBlob);
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link); // Append to DOM for Firefox compatibility
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
    return { success: true, data: successMessage };
  } catch (error: unknown) {
    /**
     * Processes an Axios error to extract a meaningful message
     *
     * @param error - The error object from the failed download attempt
     * @returns A string containing an extracted or default error message.
     */
    const processError = async (error: AxiosError) => {
      console.warn("DOWNLOAD FILE ERROR");
      let message = errorMessage;
      if (blob && error.response?.data) {
        message = (
          JSON.parse(await new Response(error.response?.data as Blob).text()) as {
            message: string;
          }
        )?.message;
      } else {
        message = (error.response?.data as { message: string })?.message;
      }
      return message;
    };
    return { success: false, errorText: await processError(error as AxiosError) };
  }
};
